import React from 'react';
import { RefreshCw, Trophy, Building2, FileBarChart, Leaf } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Spinner } from '../ui/spinner';
import { formatSnapshotTime } from './formatSnapshotTime';
import { formatOwnerLabel } from './ownerDisplay';

const formatTco2e = (n) => {
  if (n == null || Number.isNaN(Number(n))) return 'Not computed';
  return `${Number(n).toFixed(3)} tCO₂e`;
};

/**
 * Surface C — Impact & Wins from consultantFirmMetrics (R-7570 / R-7571).
 */
const ImpactWinsDashboard = ({ impact, loading = false, onRefresh }) => {
  if (loading && !impact) {
    return (
      <div className='flex justify-center py-12'>
        <Spinner />
      </div>
    );
  }

  const scale = impact?.scale || {};
  const outcomes = impact?.outcomes || {};
  const byOwner = impact?.byOwner || [];
  const stories = impact?.clientStories || [];
  const deliveredOk = outcomes.deliveredBasis === 'report_summary' && outcomes.deliveredReductionsTco2e != null;

  const decreases = stories.filter((s) => s.netMovementTco2e != null && s.netMovementTco2e < 0);
  const increases = stories.filter((s) => s.netMovementTco2e != null && s.netMovementTco2e > 0);
  const netPortfolio =
    stories.length && stories.every((s) => s.netMovementTco2e == null)
      ? null
      : stories.reduce((sum, s) => sum + (Number(s.netMovementTco2e) || 0), 0);

  return (
    <div className='flex flex-col gap-4'>
      <div className='flex flex-wrap items-center justify-between gap-3'>
        <div>
          <h1 className='text-xl font-semibold text-foreground flex items-center gap-2'>
            <Trophy size={22} strokeWidth={1.5} />
            Impact & Wins
          </h1>
          <p className='text-sm text-muted-foreground'>
            Firm rollup · computed {formatSnapshotTime(impact?.computedAt)}
          </p>
        </div>
        <Button variant='outline' size='sm' onClick={onRefresh} disabled={loading}>
          <RefreshCw size={16} strokeWidth={1.5} />
          Refresh
        </Button>
      </div>

      <div className='grid grid-cols-2 md:grid-cols-4 gap-3'>
        <Card className='bg-card'>
          <CardContent className='p-4'>
            <div className='flex items-center gap-2 text-muted-foreground mb-1'>
              <Building2 size={16} strokeWidth={1.5} />
              <span className='text-xs'>Clients helped</span>
            </div>
            <p className='text-2xl font-bold'>{scale.businessesHelped ?? 0}</p>
          </CardContent>
        </Card>
        <Card className='bg-card'>
          <CardContent className='p-4'>
            <div className='flex items-center gap-2 text-muted-foreground mb-1'>
              <FileBarChart size={16} strokeWidth={1.5} />
              <span className='text-xs'>Reports generated</span>
            </div>
            <p className='text-2xl font-bold'>{scale.reportsGenerated ?? 0}</p>
          </CardContent>
        </Card>
        <Card className='bg-card'>
          <CardContent className='p-4'>
            <div className='flex items-center gap-2 text-muted-foreground mb-1'>
              <Leaf size={16} strokeWidth={1.5} />
              <span className='text-xs'>Delivered reductions</span>
            </div>
            <p className='text-2xl font-bold'>
              {deliveredOk ? formatTco2e(outcomes.deliveredReductionsTco2e) : 'Not computed'}
            </p>
            <p className='text-xs text-muted-foreground mt-1'>
              {outcomes.note ||
                'Delivered reductions only count clients with two report summaries that are signed off or have nothing definitely missing'}
            </p>
          </CardContent>
        </Card>
        <Card className='bg-card'>
          <CardContent className='p-4'>
            <p className='text-xs text-muted-foreground mb-1'>Identified reductions</p>
            <p className='text-2xl font-bold'>Not computed</p>
            <p className='text-xs text-muted-foreground mt-1'>Never merged with Delivered</p>
          </CardContent>
        </Card>
      </div>

      <Card className='bg-card'>
        <CardHeader className='pb-2'>
          <CardTitle className='text-sm'>Net portfolio movement</CardTitle>
        </CardHeader>
        <CardContent>
          <p className='text-2xl font-bold mb-2'>
            {netPortfolio == null ? 'Not computed' : formatTco2e(netPortfolio)}
          </p>
          <p className='text-sm text-muted-foreground'>
            Decreases {decreases.length} · Increases {increases.length} · Unsigned / incomplete clients excluded from
            Delivered
          </p>
        </CardContent>
      </Card>

      <Card className='bg-card'>
        <CardHeader className='pb-2'>
          <CardTitle className='text-sm'>Client stories</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow className='border-border'>
                <TableHead>Client</TableHead>
                <TableHead>Net movement</TableHead>
                <TableHead>Delivered</TableHead>
                <TableHead>Identified</TableHead>
                <TableHead>Maturity</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {stories.map((row) => (
                <TableRow key={row.clientOrgId} className='border-border'>
                  <TableCell className='font-medium'>{row.name || 'Unnamed client'}</TableCell>
                  <TableCell>{formatTco2e(row.netMovementTco2e)}</TableCell>
                  <TableCell>{formatTco2e(row.deliveredReductionsTco2e)}</TableCell>
                  <TableCell>Not computed</TableCell>
                  <TableCell>Not computed</TableCell>
                </TableRow>
              ))}
              {stories.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className='text-center text-muted-foreground py-4'>
                    No client stories yet.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card className='bg-card'>
        <CardHeader className='pb-2'>
          <CardTitle className='text-sm'>Maturity</CardTitle>
        </CardHeader>
        <CardContent>
          <p className='text-sm text-muted-foreground'>Not computed</p>
        </CardContent>
      </Card>

      <Card className='bg-card'>
        <CardHeader className='pb-2'>
          <CardTitle className='text-sm'>By account manager</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow className='border-border'>
                <TableHead>Account manager</TableHead>
                <TableHead>Clients</TableHead>
                <TableHead>Reports</TableHead>
                <TableHead>Delivered</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {byOwner.map((row) => (
                <TableRow key={row.ownerUserId || 'unassigned'} className='border-border'>
                  <TableCell>{formatOwnerLabel(row)}</TableCell>
                  <TableCell>{row.clientCount ?? 0}</TableCell>
                  <TableCell>{row.reportsCount ?? 0}</TableCell>
                  <TableCell>{formatTco2e(row.deliveredReductionsTco2e)}</TableCell>
                </TableRow>
              ))}
              {byOwner.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} className='text-center text-muted-foreground py-4'>
                    No owner rollup yet.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default ImpactWinsDashboard;
